import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { vetrineService } from '../../services/vetrineService';

const AdminStorefrontForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract ID from hash routing URL (/#/admin-storefront-form?id=...)
  const getStoreIdFromUrl = () => {
    const hash = window.location?.hash; // e.g., "#/admin-storefront-form?id=123"
    const queryStart = hash?.indexOf('?');
    if (queryStart === -1) return null;
    
    const queryString = hash?.substring(queryStart + 1); // "id=123"
    const params = new URLSearchParams(queryString);
    return params?.get('id');
  };
  
  const storeId = getStoreIdFromUrl();
  const isEditMode = !!storeId;

  console.log('🔍 URL Analysis:', {
    fullUrl: window.location?.href,
    hash: window.location?.hash,
    extractedId: storeId,
    isEditMode
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [zones, setZones] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [dragActive, setDragActive] = useState({});

  const [formData, setFormData] = useState({
    nomeNegozio: '',
    platformTarget: 'cliccami',
    categoria: '',
    zona: '',
    indirizzo: '',
    telefono: '',
    email: '',
    whatsapp: '',
    orariApertura: '',
    descrizioneBreve: '',
    status: 'bozza',
    inEvidenza: false,
    // REMOVED: promoAssociata: null,
    tipoVetrina: 'reale'
  });

  const [images, setImages] = useState({
    logoNegozio: null,
    immaginePrincipale: null,
    galleryProdotti: []
  });

  const [imagePreviews, setImagePreviews] = useState({
    logoNegozio: null,
    immaginePrincipale: null,
    galleryProdotti: []
  });

  const [uploadingImages, setUploadingImages] = useState(false);

  // Add isSubmitting computed state for better readability
  const isSubmitting = saving || uploadingImages;

  const loadFormData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔄 Loading form data...');
      console.log('🔑 Store ID:', storeId);
      console.log('✏️ Is Edit Mode:', isEditMode);

      // ONLY 3 SIMPLE QUERIES - NO EMBEDS
      const [categoriesData, zonesData] = await Promise.all([
        vetrineService?.getCategories(),
        vetrineService?.getZones()
        // REMOVED: promotionService?.getActivePromotions()
      ]);

      console.log('📦 Categories loaded:', categoriesData);
      console.log('📦 Zones loaded:', zonesData);
      // REMOVED: console.log('📦 Promotions loaded:', promotionsData);

      if (!categoriesData || categoriesData?.length === 0) {
        console.warn('⚠️ No categories found in database');
        setError('Attenzione: Nessuna categoria trovata nel database. Contatta l\'amministratore.');
      }

      if (!zonesData || zonesData?.length === 0) {
        console.warn('⚠️ No zones found in database');
        setError('Attenzione: Nessuna zona trovata nel database. Contatta l\'amministratore.');
      }

      setCategories(categoriesData || []);
      setZones(zonesData || []);
      // REMOVED: setPromotions(promotionsData || []);

      if (isEditMode && storeId) {
        console.log('🔍 Edit mode detected, loading store ID:', storeId);
        try {
          const storeData = await vetrineService?.getStoreById(storeId);
          console.log('📦 Store data loaded:', storeData);
          
          if (!storeData) {
            throw new Error('Vetrina non trovata');
          }
          
          setFormData({
            nomeNegozio: storeData?.nomeNegozio || '',
            platformTarget: storeData?.platformTarget || 'cliccami',
            categoria: storeData?.categoria || '',
            zona: storeData?.zona || '',
            indirizzo: storeData?.indirizzo || '',
            telefono: storeData?.telefono || '',
            email: storeData?.email || '',
            whatsapp: storeData?.whatsapp || '',
            orariApertura: storeData?.orariApertura || '',
            descrizioneBreve: storeData?.descrizioneBreve || '',
            status: storeData?.status || 'bozza',
            inEvidenza: storeData?.inEvidenza || false,
            // REMOVED: promoAssociata: storeData?.promoAssociata || null,
            tipoVetrina: storeData?.tipoVetrina || 'reale'
          });

          // Set existing image previews - images are already full URLs from database
          if (storeData?.logoNegozio) {
            setImagePreviews(prev => ({
              ...prev,
              logoNegozio: storeData?.logoNegozio
            }));
          }
          if (storeData?.immaginePrincipale) {
            setImagePreviews(prev => ({
              ...prev,
              immaginePrincipale: storeData?.immaginePrincipale
            }));
          }
          if (storeData?.galleryProdotti?.length > 0) {
            setImagePreviews(prev => ({
              ...prev,
              galleryProdotti: storeData?.galleryProdotti
            }));
          }
          console.log('✅ Edit mode data loaded successfully');
          console.log('✅ Form data set to:', {
            nomeNegozio: storeData?.nomeNegozio,
            categoria: storeData?.categoria,
            zona: storeData?.zona
          });
        } catch (editError) {
          console.error('❌ Error loading store data:', editError);
          setError(`Errore nel caricamento della vetrina: ${editError?.message}`);
        }
      }
    } catch (err) {
      console.error('❌ Error in loadFormData:', err);
      setError(err?.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
      console.log('🏁 loadFormData completed');
    }
  }, [storeId, isEditMode]);

  useEffect(() => {
    console.log('🎯 AdminStorefrontForm mounted');
    console.log('📍 Current URL:', window.location?.href);
    console.log('🔑 Store ID from params:', storeId);
    console.log('✏️ Is Edit Mode:', isEditMode);
    loadFormData();
  }, [storeId, isEditMode, loadFormData]);

  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'nomeNegozio':
        if (!value?.trim()) {
          errors.nomeNegozio = 'Il nome del negozio è obbligatorio';
        } else if (value?.trim()?.length < 3) {
          errors.nomeNegozio = 'Il nome deve contenere almeno 3 caratteri';
        } else if (value?.trim()?.length > 100) {
          errors.nomeNegozio = 'Il nome non può superare 100 caratteri';
        }
        break;
      case 'categoria':
        if (!value) {
          errors.categoria = 'La categoria è obbligatoria';
        }
        break;
      case 'zona':
        if (!value) {
          errors.zona = 'La zona è obbligatoria';
        }
        break;
      case 'indirizzo':
        if (!value?.trim()) {
          errors.indirizzo = 'L\'indirizzo è obbligatorio';
        } else if (value?.trim()?.length < 10) {
          errors.indirizzo = 'Inserisci un indirizzo completo';
        }
        break;
      case 'telefono':
        if (!value?.trim()) {
          errors.telefono = 'Il telefono è obbligatorio';
        } else if (!/^[+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/?.test(value)) {
          errors.telefono = 'Inserisci un numero di telefono valido';
        }
        break;
      case 'email':
        if (value?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) {
          errors.email = 'Inserisci un indirizzo email valido';
        }
        break;
      case 'whatsapp':
        if (value?.trim() && !/^[+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/?.test(value)) {
          errors.whatsapp = 'Inserisci un numero WhatsApp valido';
        }
        break;
      case 'orariApertura':
        if (!value?.trim()) {
          errors.orariApertura = 'Gli orari sono obbligatori';
        } else if (value?.trim()?.length < 5) {
          errors.orariApertura = 'Inserisci gli orari completi';
        }
        break;
      case 'descrizioneBreve':
        if (!value?.trim()) {
          errors.descrizioneBreve = 'La descrizione è obbligatoria';
        } else if (value?.trim()?.length < 20) {
          errors.descrizioneBreve = 'La descrizione deve contenere almeno 20 caratteri';
        } else if (value?.trim()?.length > 500) {
          errors.descrizioneBreve = 'La descrizione non può superare 500 caratteri';
        }
        break;
    }

    return errors;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation
    const errors = validateField(field, value);
    setFieldErrors(prev => ({
      ...prev,
      ...errors,
      ...(Object.keys(errors)?.length === 0 && { [field]: undefined })
    }));
    
    setError('');
  };

  const handleDrag = (e, field) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(prev => ({ ...prev, [field]: true }));
    } else if (e?.type === 'dragleave') {
      setDragActive(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleDrop = (e, field) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(prev => ({ ...prev, [field]: false }));

    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleImageSelect(field, files);
    }
  };

  const handleImageSelect = (field, files) => {
    if (field === 'galleryProdotti') {
      const fileArray = Array.from(files);
      const validFiles = fileArray?.filter(file => file?.type?.startsWith('image/'));
      
      if (validFiles?.length !== fileArray?.length) {
        setError('Alcuni file non sono immagini valide');
        setTimeout(() => setError(''), 3000);
      }

      setImages(prev => ({ ...prev, [field]: [...(prev?.[field] || []), ...validFiles] }));

      // Create previews
      const previewUrls = validFiles?.map(file => URL.createObjectURL(file));
      setImagePreviews(prev => ({ 
        ...prev, 
        [field]: [...(prev?.[field] || []), ...previewUrls] 
      }));
    } else {
      const file = files?.[0];
      if (file) {
        if (!file?.type?.startsWith('image/')) {
          setError('Il file selezionato non è un\'immagine valida');
          setTimeout(() => setError(''), 3000);
          return;
        }
        setImages(prev => ({ ...prev, [field]: file }));
        setImagePreviews(prev => ({ ...prev, [field]: URL.createObjectURL(file) }));
      }
    }
  };

  const removeGalleryImage = (index) => {
    setImages(prev => ({
      ...prev,
      galleryProdotti: prev?.galleryProdotti?.filter((_, i) => i !== index)
    }));
    setImagePreviews(prev => ({
      ...prev,
      galleryProdotti: prev?.galleryProdotti?.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Validate required text fields
    // TEMPORARY: Removed categoria and zona from required fields for testing
    const requiredFields = [
      { field: 'nomeNegozio', label: 'Nome Negozio' },
      // { field: 'categoria', label: 'Categoria' }, // TEMPORARILY DISABLED FOR TESTING
      // { field: 'zona', label: 'Zona' }, // TEMPORARILY DISABLED FOR TESTING
      { field: 'indirizzo', label: 'Indirizzo' },
      { field: 'telefono', label: 'Telefono' },
      { field: 'orariApertura', label: 'Orari' },
      { field: 'descrizioneBreve', label: 'Descrizione' }
    ];

    requiredFields?.forEach(({ field, label }) => {
      const fieldErrors = validateField(field, formData?.[field]);
      Object.assign(errors, fieldErrors);
    });

    // Validate images (minimum 3 total)
    const totalImages = (
      (images?.logoNegozio instanceof File ? 1 : (imagePreviews?.logoNegozio ? 1 : 0)) +
      (images?.immaginePrincipale instanceof File ? 1 : (imagePreviews?.immaginePrincipale ? 1 : 0)) +
      (images?.galleryProdotti?.length || 0) +
      (imagePreviews?.galleryProdotti?.length || 0)
    );

    if (!isEditMode && totalImages < 3) {
      errors.images = 'Carica almeno 3 immagini (logo, immagine principale, o galleria)';
    }

    setFieldErrors(errors);

    if (Object.keys(errors)?.length > 0) {
      setError('Correggi gli errori nel form prima di salvare');
      // Scroll to first error
      const firstErrorElement = document.querySelector('.border-red-500');
      if (firstErrorElement) {
        firstErrorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    // TEMPORARY: Log warning if categoria or zona are empty
    if (!formData?.categoria || !formData?.zona) {
      console.warn('⚠️ TESTING MODE: Form submitted without categoria or zona');
      console.log('Categoria:', formData?.categoria || 'EMPTY');
      console.log('Zona:', formData?.zona || 'EMPTY');
    }

    return true;
  };

  const uploadImages = async () => {
    const uploadedPaths = {};

    try {
      setUploadingImages(true);

      // Upload logo if new file
      if (images?.logoNegozio instanceof File) {
        console.log('📤 Uploading logo...');
        const path = await vetrineService?.uploadImage(images?.logoNegozio, 'logos');
        const publicUrl = vetrineService?.getImageUrl(path);
        uploadedPaths.logoNegozio = publicUrl;
        console.log('✅ Logo uploaded:', publicUrl);
      } else if (imagePreviews?.logoNegozio) {
        // Preserve existing logo URL
        uploadedPaths.logoNegozio = imagePreviews?.logoNegozio;
        console.log('✅ Logo preserved:', imagePreviews?.logoNegozio);
      }

      // Upload main image if new file
      if (images?.immaginePrincipale instanceof File) {
        console.log('📤 Uploading main image...');
        const path = await vetrineService?.uploadImage(images?.immaginePrincipale, 'main');
        const publicUrl = vetrineService?.getImageUrl(path);
        uploadedPaths.immaginePrincipale = publicUrl;
        console.log('✅ Main image uploaded:', publicUrl);
      } else if (imagePreviews?.immaginePrincipale) {
        // Preserve existing main image URL
        uploadedPaths.immaginePrincipale = imagePreviews?.immaginePrincipale;
        console.log('✅ Main image preserved:', imagePreviews?.immaginePrincipale);
      }

      // Upload gallery images if new files
      const galleryUrls = [];
      
      // First, preserve existing gallery images (URLs from database)
      if (imagePreviews?.galleryProdotti?.length > 0) {
        imagePreviews?.galleryProdotti?.forEach(preview => {
          // Only add if it's a URL (existing image), not a blob URL (new upload)
          if (preview?.startsWith('http://') || preview?.startsWith('https://')) {
            galleryUrls?.push(preview);
          }
        });
        console.log('✅ Preserved existing gallery images:', galleryUrls?.length);
      }
      
      // Then, upload new gallery images
      if (images?.galleryProdotti?.length > 0) {
        console.log('📤 Uploading new gallery images...');
        for (const file of images?.galleryProdotti) {
          if (file instanceof File) {
            const path = await vetrineService?.uploadImage(file, 'gallery');
            const publicUrl = vetrineService?.getImageUrl(path);
            galleryUrls?.push(publicUrl);
          }
        }
        console.log('✅ New gallery images uploaded:', images?.galleryProdotti?.length);
      }
      
      if (galleryUrls?.length > 0) {
        uploadedPaths.galleryProdotti = galleryUrls;
        console.log('✅ Total gallery images:', galleryUrls?.length);
      }

      return uploadedPaths;
    } catch (err) {
      console.error('❌ Image upload error:', err);
      throw new Error(`Errore nel caricamento delle immagini: ${err?.message}`);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSubmit = async (saveAsDraft = false) => {
    try {
      console.log('🚀 Starting form submission...');
      console.log('📋 Form data:', formData);
      console.log('🖼️ Images:', { 
        logo: !!images?.logoNegozio, 
        main: !!images?.immaginePrincipale, 
        gallery: images?.galleryProdotti?.length 
      });

      // Validate form
      if (!validateForm()) {
        console.warn('⚠️ Validation failed');
        return;
      }

      setSaving(true);
      setError('');
      setSuccess('');

      // Upload new images
      console.log('📤 Uploading images...');
      const uploadedPaths = await uploadImages();
      console.log('✅ Images uploaded:', uploadedPaths);

      // Prepare submission data
      const submitData = {
        nomeNegozio: formData?.nomeNegozio?.trim(),
        platformTarget: formData?.platformTarget,
        categoria: formData?.categoria,
        zona: formData?.zona,
        indirizzo: formData?.indirizzo?.trim(),
        telefono: formData?.telefono?.trim(),
        email: formData?.email?.trim() || null,
        whatsapp: formData?.whatsapp?.trim() || null,
        orariApertura: formData?.orariApertura?.trim(),
        descrizioneBreve: formData?.descrizioneBreve?.trim(),
        status: saveAsDraft ? 'bozza' : formData?.status,
        inEvidenza: formData?.inEvidenza,
        // REMOVED: promoAssociata: formData?.promoAssociata || null,
        tipoVetrina: formData?.tipoVetrina,
        ...uploadedPaths
      };

      console.log('💾 Submitting data to Supabase:', submitData);
      console.log('🎯 platform_target being saved:', formData?.platformTarget);
      console.log('📋 Form data:', formData);

      let result;
      if (isEditMode) {
        console.log('🔄 Updating existing store:', storeId);
        result = await vetrineService?.updateStore(storeId, submitData);
        toast?.success('Vetrina aggiornata con successo!');
      } else {
        console.log('➕ Creating new store');
        result = await vetrineService?.createStore(submitData);
        toast?.success('Vetrina creata con successo!');
      }

      console.log('✅ Submission successful:', result);

      // Navigate after short delay to show success message
      setTimeout(() => {
        navigate('/admin-vetrine');
      }, 1500);
    } catch (err) {
      console.error('❌ Submission error:', err);
      toast?.error(err?.message || 'Errore nel salvataggio della vetrina. Riprova.');
      
      // Scroll to top for visibility
      window?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const getCategoryLabel = (value) => {
    return categories?.find(cat => cat?.value === value)?.label || value;
  };

  const getZoneLabel = (value) => {
    return zones?.find(zone => zone?.value === value)?.label || value;
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Caricamento... - Admin Vetrine</title>
        </Helmet>
        <div className="flex flex-col min-h-screen bg-gray-50">
          <Header onFilterClick={() => {}} />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Icon name="loader" className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Caricamento...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? 'Modifica Vetrina' : 'Nuova Vetrina'} - Admin</title>
      </Helmet>
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header onFilterClick={() => {}} />
        
        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4">
              <Icon name="loader" className="w-12 h-12 animate-spin text-blue-600" />
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {uploadingImages ? 'Caricamento immagini...' : 'Salvataggio in corso...'}
                </h3>
                <p className="text-sm text-gray-600">
                  {uploadingImages 
                    ? 'Stiamo caricando le tue immagini. Attendere prego.' 
                    : 'Stiamo salvando la tua vetrina. Non chiudere questa pagina.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/admin-vetrine')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            >
              <Icon name="arrow_back" className="w-5 h-5" />
              <span>Torna alle vetrine</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? 'Modifica Vetrina' : 'Nuova Vetrina'}
            </h1>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <Icon name="error" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Icon name="check_circle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-green-800">{success}</p>
            </div>
          )}

          {/* Two Column Layout: Form + Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
                {/* Basic Information */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Informazioni Base</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Negozio <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={formData?.nomeNegozio}
                        onChange={(e) => handleInputChange('nomeNegozio', e?.target?.value)}
                        placeholder="Es: Pizzeria Da Mario"
                        className={fieldErrors?.nomeNegozio ? 'border-red-500' : ''}
                        disabled={isSubmitting}
                      />
                      {fieldErrors?.nomeNegozio && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.nomeNegozio}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Piattaforma
                      </label>
                      <select
                        value={formData?.platformTarget}
                        onChange={(e) => handleInputChange('platformTarget', e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                      >
                        <option value="cliccami">cliccami</option>
                        <option value="unidaymc">unidaymc</option>
                        <option value="both">both</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoria <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData?.categoria}
                        onChange={(e) => handleInputChange('categoria', e?.target?.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors?.categoria ? 'border-red-500' : 'border-gray-300'}`}
                        disabled={loading || categories?.length === 0 || isSubmitting}
                      >
                        <option value="">
                          {loading ? 'Caricamento...' : categories?.length === 0 ? 'Nessuna categoria disponibile' : 'Seleziona categoria'}
                        </option>
                        {categories?.map(cat => (
                          <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                        ))}
                      </select>
                      {fieldErrors?.categoria && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.categoria}</p>
                      )}
                      {!loading && categories?.length === 0 && (
                        <p className="mt-1 text-sm text-amber-600 flex items-center gap-1">
                          <Icon name="warning" className="w-4 h-4" />
                          Le categorie non sono state caricate. Verifica la connessione al database.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zona <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData?.zona}
                        onChange={(e) => handleInputChange('zona', e?.target?.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors?.zona ? 'border-red-500' : 'border-gray-300'}`}
                        disabled={loading || zones?.length === 0 || isSubmitting}
                      >
                        <option value="">
                          {loading ? 'Caricamento...' : zones?.length === 0 ? 'Nessuna zona disponibile' : 'Seleziona zona'}
                        </option>
                        {zones?.map(zone => (
                          <option key={zone?.value} value={zone?.value}>{zone?.label}</option>
                        ))}
                      </select>
                      {fieldErrors?.zona && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.zona}</p>
                      )}
                      {!loading && zones?.length === 0 && (
                        <p className="mt-1 text-sm text-amber-600 flex items-center gap-1">
                          <Icon name="warning" className="w-4 h-4" />
                          Le zone non sono state caricate. Verifica la connessione al database.
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Indirizzo <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={formData?.indirizzo}
                        onChange={(e) => handleInputChange('indirizzo', e?.target?.value)}
                        placeholder="Via Roma 123, Milano"
                        className={fieldErrors?.indirizzo ? 'border-red-500' : ''}
                        disabled={isSubmitting}
                      />
                      {fieldErrors?.indirizzo && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.indirizzo}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefono <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={formData?.telefono}
                        onChange={(e) => handleInputChange('telefono', e?.target?.value)}
                        placeholder="+39 02 1234567"
                        className={fieldErrors?.telefono ? 'border-red-500' : ''}
                        disabled={isSubmitting}
                      />
                      {fieldErrors?.telefono && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.telefono}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={formData?.email}
                        onChange={(e) => handleInputChange('email', e?.target?.value)}
                        placeholder="info@negozio.it"
                        className={fieldErrors?.email ? 'border-red-500' : ''}
                        disabled={isSubmitting}
                      />
                      {fieldErrors?.email && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        WhatsApp
                      </label>
                      <Input
                        value={formData?.whatsapp}
                        onChange={(e) => handleInputChange('whatsapp', e?.target?.value)}
                        placeholder="+39 333 1234567"
                        className={fieldErrors?.whatsapp ? 'border-red-500' : ''}
                        disabled={isSubmitting}
                      />
                      {fieldErrors?.whatsapp && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.whatsapp}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Orari <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={formData?.orariApertura}
                        onChange={(e) => handleInputChange('orariApertura', e?.target?.value)}
                        placeholder="Lun-Ven: 9:00-19:00, Sab: 9:00-13:00"
                        className={fieldErrors?.orariApertura ? 'border-red-500' : ''}
                        disabled={isSubmitting}
                      />
                      {fieldErrors?.orariApertura && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.orariApertura}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrizione Breve <span className="text-red-500">*</span>
                        <span className="text-gray-500 font-normal ml-2">
                          ({formData?.descrizioneBreve?.length || 0}/500)
                        </span>
                      </label>
                      <textarea
                        value={formData?.descrizioneBreve}
                        onChange={(e) => handleInputChange('descrizioneBreve', e?.target?.value)}
                        placeholder="Breve descrizione del negozio..."
                        rows={4}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          fieldErrors?.descrizioneBreve ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {fieldErrors?.descrizioneBreve && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors?.descrizioneBreve}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Images Section with Drag & Drop */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Immagini <span className="text-sm font-normal text-gray-600">(minimo 3 immagini)</span>
                  </h2>
                  <div className="space-y-4">
                    {/* Logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Logo Negozio
                      </label>
                      <div
                        onDragEnter={(e) => handleDrag(e, 'logoNegozio')}
                        onDragLeave={(e) => handleDrag(e, 'logoNegozio')}
                        onDragOver={(e) => handleDrag(e, 'logoNegozio')}
                        onDrop={(e) => handleDrop(e, 'logoNegozio')}
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          dragActive?.logoNegozio 
                            ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                        } ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        {imagePreviews?.logoNegozio ? (
                          <div className="flex flex-col items-center gap-3">
                            <img
                              src={imagePreviews?.logoNegozio}
                              alt="Logo preview"
                              className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                            />
                            <Button
                              onClick={() => {
                                setImages(prev => ({ ...prev, logoNegozio: null }));
                                setImagePreviews(prev => ({ ...prev, logoNegozio: null }));
                              }}
                              variant="outline"
                              size="sm"
                            >
                              Rimuovi
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Icon name="cloud_upload" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-600 mb-2">
                              Trascina qui il logo o clicca per selezionare
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageSelect('logoNegozio', e?.target?.files)}
                              className="hidden"
                              id="logo-upload"
                              disabled={isSubmitting}
                            />
                            <label
                              htmlFor="logo-upload"
                              className="inline-block px-4 py-2 bg-blue-50 text-blue-700 rounded-lg cursor-pointer hover:bg-blue-100 text-sm font-medium"
                            >
                              Seleziona File
                            </label>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Main Image */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Immagine Principale
                      </label>
                      <div
                        onDragEnter={(e) => handleDrag(e, 'immaginePrincipale')}
                        onDragLeave={(e) => handleDrag(e, 'immaginePrincipale')}
                        onDragOver={(e) => handleDrag(e, 'immaginePrincipale')}
                        onDrop={(e) => handleDrop(e, 'immaginePrincipale')}
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          dragActive?.immaginePrincipale 
                            ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                        } ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        {imagePreviews?.immaginePrincipale ? (
                          <div className="flex flex-col items-center gap-3">
                            <img
                              src={imagePreviews?.immaginePrincipale}
                              alt="Main image preview"
                              className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-200"
                            />
                            <Button
                              onClick={() => {
                                setImages(prev => ({ ...prev, immaginePrincipale: null }));
                                setImagePreviews(prev => ({ ...prev, immaginePrincipale: null }));
                              }}
                              variant="outline"
                              size="sm"
                            >
                              Rimuovi
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Icon name="cloud_upload" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-600 mb-2">
                              Trascina qui l'immagine principale o clicca per selezionare
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageSelect('immaginePrincipale', e?.target?.files)}
                              className="hidden"
                              id="main-upload"
                              disabled={isSubmitting}
                            />
                            <label
                              htmlFor="main-upload"
                              className="inline-block px-4 py-2 bg-blue-50 text-blue-700 rounded-lg cursor-pointer hover:bg-blue-100 text-sm font-medium"
                            >
                              Seleziona File
                            </label>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Gallery */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Galleria Prodotti (multipla)
                      </label>
                      <div
                        onDragEnter={(e) => handleDrag(e, 'galleryProdotti')}
                        onDragLeave={(e) => handleDrag(e, 'galleryProdotti')}
                        onDragOver={(e) => handleDrag(e, 'galleryProdotti')}
                        onDrop={(e) => handleDrop(e, 'galleryProdotti')}
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          dragActive?.galleryProdotti 
                            ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                        } ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <Icon name="cloud_upload" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-2">
                          Trascina qui le immagini o clicca per selezionare (multipla)
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleImageSelect('galleryProdotti', e?.target?.files)}
                          className="hidden"
                          id="gallery-upload"
                          disabled={isSubmitting}
                        />
                        <label
                          htmlFor="gallery-upload"
                          className="inline-block px-4 py-2 bg-blue-50 text-blue-700 rounded-lg cursor-pointer hover:bg-blue-100 text-sm font-medium"
                        >
                          Seleziona File
                        </label>
                      </div>
                      {imagePreviews?.galleryProdotti?.length > 0 && (
                        <div className="mt-4 grid grid-cols-4 gap-4">
                          {imagePreviews?.galleryProdotti?.map((preview, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={preview}
                                alt={`Gallery ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border border-gray-200"
                              />
                              <button
                                type="button"
                                onClick={() => removeGalleryImage(index)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Icon name="close" className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {fieldErrors?.images && (
                      <p className="text-sm text-red-600">{fieldErrors?.images}</p>
                    )}
                  </div>
                </div>

                {/* Status & Settings */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Impostazioni</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo Vetrina <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="tipoVetrina"
                            value="demo"
                            checked={formData?.tipoVetrina === 'demo'}
                            onChange={(e) => handleInputChange('tipoVetrina', e?.target?.value)}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            disabled={isSubmitting}
                          />
                          <span className="text-sm text-gray-700">Demo</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="tipoVetrina"
                            value="reale"
                            checked={formData?.tipoVetrina === 'reale'}
                            onChange={(e) => handleInputChange('tipoVetrina', e?.target?.value)}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            disabled={isSubmitting}
                          />
                          <span className="text-sm text-gray-700">Reale</span>
                        </label>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Le vetrine demo sono esempi per i clienti, quelle reali sono confermate
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stato Vetrina <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={formData?.status}
                        onChange={(e) => handleInputChange('status', e?.target?.value)}
                        disabled={isSubmitting}
                      >
                        <option value="attiva">Attiva</option>
                        <option value="bozza">Bozza</option>
                        <option value="disattivata">Disattivata</option>
                      </Select>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="inEvidenza"
                        checked={formData?.inEvidenza}
                        onChange={(e) => handleInputChange('inEvidenza', e?.target?.checked)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="inEvidenza" className="text-sm font-medium text-gray-700">
                        In Evidenza
                      </label>
                    </div>

                    {/* PROMOTIONS BLOCK - TEMPORARILY DISABLED */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Promo Associata (opzionale)
                      </label>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800 font-medium">
                          ⚠️ Promozioni disattivate temporaneamente
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                  <Button
                    onClick={() => navigate('/admin-vetrine')}
                    variant="outline"
                    disabled={isSubmitting}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={() => handleSubmit(true)}
                    variant="outline"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <Icon name="loader" className="w-4 h-4 animate-spin" />
                        Salvataggio...
                      </span>
                    ) : (
                      'Salva come Bozza'
                    )}
                  </Button>
                  <Button
                    onClick={() => handleSubmit(false)}
                    disabled={isSubmitting}
                  >
                    {uploadingImages ? (
                      <span className="flex items-center gap-2">
                        <Icon name="loader" className="w-4 h-4 animate-spin" />
                        Caricamento immagini...
                      </span>
                    ) : saving ? (
                      <span className="flex items-center gap-2">
                        <Icon name="loader" className="w-4 h-4 animate-spin" />
                        Salvataggio...
                      </span>
                    ) : (
                      'Salva'
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Real-Time Preview Panel - TEMPORARILY DISABLED */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Icon name="visibility" className="w-5 h-5" />
                    Anteprima Live
                  </h2>
                  
                  {/* TEMPORARILY DISABLED MESSAGE */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <Icon name="warning" className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                    <p className="text-yellow-800 font-medium mb-2">
                      Anteprima temporaneamente disattivata
                    </p>
                    <p className="text-sm text-yellow-700">
                      La funzionalità di anteprima live sarà riattivata a breve
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminStorefrontForm;